import {FormPersonalInfo} from "./FormPersonalInfo";
import {FormPersonalInfoActivity} from "./FormPersonalInfoActivity";
import {FormPersonalInfoMobile} from "./FormPersonalInfoMobile";
import {FormPersonalInfoActivityMobile} from "./FormPersonalInfoActivityMobile";
import {Desktop, LandscapePhonesAndSmaller, LandscapeTablet} from "../../../utils/breakpoints";
import {AplicantOptions} from "./ApplicantOptions";
import { useIntl } from "react-intl";
import { useRequestContext } from "../../../hooks/useRequestContext";
import { useEffect } from "react";
import ClockLoader from "react-spinners/ClockLoader";
import {useState} from 'react';
import Moment from 'moment';
import { WLOCreditAPIService } from "../../../services";
import type { CygnusPersona } from "../../../context/RequestContext";

interface PersonalInfoContainerProps {
    errors?: any;
    values?: any;
    setValues?: (values: any) => void;
    setPopup: React.Dispatch<React.SetStateAction<string>>;
}

/** Campos de persona/actividad que deben venir del WS (no del formulario anterior). */
const emptyPersonaWsFields: Record<string, string> = {
    Pws_Estado_Civil: '',
    Pws_Direccion_res: '',
    Pws_Departamento: '',
    Pws_Ciudad_res: '',
    Pws_Corregimiento: '',
    Pws_Genero: '',
    Pws_Estrato_per: '',
    Pws_Per_cargo: '',
    Pws_Tip_vivienda: '',
    Pws_Niv_estudio: '',
    Pws_Nom_empre: '',
    Pws_fec_ingemp: '',
    Pws_Tip_contra: '',
    Pws_Ant_labo: '',
    Pws_Car_emp: '',
    Pws_Nom_Jefedi: '',
    Pws_Direc_emp: '',
    Pws_Ciud_emp: '',
    Pws_tel_emp1: '',
    Pws_tel_emp2: '',
    Pws_Fec_nacime: '',
    Pws_Fec_expe: '',
    Pws_Lugar_exp: '',
    Pws_Tip_person: '',
    Pws_Tip_ocupa: '',
    Pws_Fideliza: '',
    s_nombre: '',
};

const normalizeSoapDate = (raw?: string) => {
    if (!raw) return '';
    const cleaned = raw.includes('-00') ? raw.split('-00')[0] : raw;
    const parsed = Moment(new Date(cleaned));
    return parsed.isValid() ? parsed.format('yyyy-MM-DD') : '';
};

const mapCygnusPersona = (p: any): CygnusPersona => ({
    Pws_Identificacion: p.Pws_Identificacion ?? '',
    Pws_Tip_Identificacion: p.Pws_Tip_Identificacion ?? '',
    Pws_Tip_Estado: p.Pws_Tip_Estado ?? '',
    Pws_Tip_Perfil: p.Pws_Tip_Perfil ?? '',
    Pws_Tip_person: p.Pws_Tip_person ?? '',
    Pws_Tip_ocupa: p.Pws_Tip_ocupa ?? '',
    Pws_Nombres: p.Pws_Nombres ?? '',
    Pws_Apellidos1: p.Pws_Apellidos1 ?? '',
    Pws_Apellidos2: p.Pws_Apellidos2 ?? '',
    Pws_Telefono1: p.Pws_Telefono1 ?? '',
    Pws_Telefono2: p.Pws_Telefono2 ?? '',
    Pws_Correo: p.Pws_Correo ?? '',
    Pws_Fec_expe: p.Pws_Fec_expe ?? '',
    Pws_Lugar_exp: p.Pws_Lugar_exp ?? '',
    Pws_Fec_nacime: p.Pws_Fec_nacime ?? '',
    Pws_Estado_Civil: p.Pws_Estado_Civil ?? '',
    Pws_Direccion_res: p.Pws_Direccion_res ?? '',
    Pws_Departamento: p.Pws_Departamento ?? '',
    Pws_Ciudad_res: p.Pws_Ciudad_res ?? '',
    Pws_Corregimiento: p.Pws_Corregimiento ?? '',
    Pws_Genero: p.Pws_Genero ?? '',
    Pws_Estrato_per: p.Pws_Estrato_per ?? '',
    Pws_Per_cargo: p.Pws_Per_cargo ?? '',
    Pws_Tip_vivienda: p.Pws_Tip_vivienda ?? '',
    Pws_Niv_estudio: p.Pws_Niv_estudio ?? '',
    Pws_Nom_empre: p.Pws_Nom_empre ?? '',
    Pws_fec_ingemp: p.Pws_fec_ingemp ?? '',
    Pws_Tip_contra: p.Pws_Tip_contra ?? '',
    Pws_Ant_labo: p.Pws_Ant_labo ?? '',
    Pws_Car_emp: p.Pws_Car_emp ?? '',
    Pws_Nom_Jefedi: p.Pws_Nom_Jefedi ?? '',
    Pws_Direc_emp: p.Pws_Direc_emp ?? '',
    Pws_Ciud_emp: p.Pws_Ciud_emp ?? '',
    Pws_tel_emp1: p.Pws_tel_emp1 ?? '',
    Pws_tel_emp2: p.Pws_tel_emp2 ?? '',
    Pws_Fideliza: p.Pws_Fideliza ?? '',
});

export function PersonalInfoContainer(props: PersonalInfoContainerProps) {
    const {errors, values, setValues, setPopup} = props;
    const intl = useIntl();
    const {request, setRequest} = useRequestContext();
    const [isLoading, setIsLoading] = useState(true);

    /** Consulta fresca al WS por documento; limpia data previa y solo puebla si Resultado === "1". */
    useEffect(() => {
        const num = request.numIdentification;
        const type = request.typeIdentification;
        if (!num || !type || !setValues) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        // Evita mostrar ficha de otra consulta mientras llega la respuesta actual.
        setValues((prev: any) => ({
            ...prev,
            ...emptyPersonaWsFields,
            Pws_Identificacion: num,
            Pws_Tip_Identificacion: type,
        }));
        setRequest({
            ...request,
            personCygnus: undefined,
            personCode: undefined,
            direcciones: undefined,
        });

        WLOCreditAPIService.consultaSolicitudCygnus({
            Pws_Identificacion: num,
            Pws_Tip_Identificacion: type,
        })
            .then((res) => {
                if (cancelled) return;

                const resultOk = String(res?.payload?.result ?? '') === '1';
                const raw = res?.payload?.data?.datos_persona?.Datos_persona;
                const datos = Array.isArray(raw) ? raw : (raw ? [raw] : []);

                if (!resultOk || datos.length === 0) {
                    setRequest({
                        ...request,
                        numIdentification: num,
                        typeIdentification: type,
                        personCygnus: undefined,
                        personCode: undefined,
                        direcciones: undefined,
                    });
                    setIsLoading(false);
                    return;
                }

                const p = datos[0];
                const personCode = p.Pws_i_codigo != null && p.Pws_i_codigo !== '' ? String(p.Pws_i_codigo) : undefined;
                const mapped = mapCygnusPersona(p);

                setRequest({
                    ...request,
                    numIdentification: num,
                    typeIdentification: type,
                    personCygnus: mapped,
                    personCode,
                    direcciones: undefined,
                    searchANI: false,
                });

                setValues((prev: any) => ({
                    ...prev,
                    Pws_Ant_labo: mapped.Pws_Ant_labo,
                    Pws_Apellidos1: mapped.Pws_Apellidos1 || prev.Pws_Apellidos1 || '',
                    Pws_Apellidos2: mapped.Pws_Apellidos2 || prev.Pws_Apellidos2 || '',
                    Pws_Car_emp: mapped.Pws_Car_emp,
                    Pws_Ciud_emp: mapped.Pws_Ciud_emp,
                    Pws_Ciudad_res: mapped.Pws_Ciudad_res,
                    Pws_Corregimiento: mapped.Pws_Corregimiento ?? '',
                    Pws_Correo: mapped.Pws_Correo || prev.Pws_Correo || '',
                    Pws_Departamento: mapped.Pws_Departamento,
                    Pws_Direc_emp: mapped.Pws_Direc_emp,
                    Pws_Direccion_res: mapped.Pws_Direccion_res,
                    Pws_Estado_Civil: mapped.Pws_Estado_Civil,
                    Pws_Estrato_per: mapped.Pws_Estrato_per,
                    Pws_Fec_expe: normalizeSoapDate(mapped.Pws_Fec_expe),
                    Pws_Fec_nacime: normalizeSoapDate(mapped.Pws_Fec_nacime),
                    Pws_Genero: mapped.Pws_Genero,
                    Pws_Identificacion: mapped.Pws_Identificacion || num,
                    Pws_Lugar_exp: mapped.Pws_Lugar_exp,
                    Pws_Niv_estudio: mapped.Pws_Niv_estudio,
                    Pws_Nom_Jefedi: mapped.Pws_Nom_Jefedi,
                    Pws_Nom_empre: mapped.Pws_Nom_empre,
                    Pws_Nombres: mapped.Pws_Nombres || prev.Pws_Nombres || '',
                    Pws_Tip_contra: mapped.Pws_Tip_contra,
                    Pws_Per_cargo: mapped.Pws_Per_cargo,
                    Pws_Telefono1: mapped.Pws_Telefono1 || prev.Pws_Telefono1 || '',
                    Pws_Telefono2: mapped.Pws_Telefono2 || prev.Pws_Telefono2 || '',
                    Pws_Tip_Estado: mapped.Pws_Tip_Estado,
                    ws_Tip_Perfil: mapped.Pws_Tip_Perfil,
                    Pws_Tip_Identificacion: mapped.Pws_Tip_Identificacion || type,
                    Pws_Tip_person: mapped.Pws_Tip_person || prev.Pws_Tip_person || '',
                    Pws_Tip_ocupa: mapped.Pws_Tip_ocupa || prev.Pws_Tip_ocupa || '',
                    Pws_Tip_vivienda: mapped.Pws_Tip_vivienda,
                    Pws_fec_ingemp: normalizeSoapDate(mapped.Pws_fec_ingemp),
                    Pws_tel_emp1: mapped.Pws_tel_emp1,
                    Pws_tel_emp2: mapped.Pws_tel_emp2,
                    Pws_Fideliza: mapped.Pws_Fideliza,
                }));
                setIsLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setRequest({
                    ...request,
                    numIdentification: num,
                    typeIdentification: type,
                    personCygnus: undefined,
                    personCode: undefined,
                    direcciones: undefined,
                });
                setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // Solo reconsultar cuando cambia el documento del paso actual.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [request.numIdentification, request.typeIdentification, setValues]);

    const renderTitle = () => {
        return ( 
            <div className="applicant-step-title">
             <ClockLoader id='spinner' color={"#4FAE32"} loading={isLoading} size={56}/>
                <h1 className="title-red">{intl.formatMessage({id: "form_applicant_information_title"})}</h1>
                <h1 className="subtitle-medium">{intl.formatMessage({id: "form_credit_information_subtitle"})}</h1>
            </div>
        )
    }

    return (
        <>
            <Desktop>
                {renderTitle()}
                <AplicantOptions values={values} errors={errors} pwsCodCredi={values.pws_cod_credi} />
                <FormPersonalInfo values={values} errors={errors} setPopup={setPopup} />
                <FormPersonalInfoActivity values={values} errors={errors}/>
            </Desktop>
            <LandscapeTablet>
            {renderTitle()}
                <AplicantOptions values={values} errors={errors} pwsCodCredi={values.pws_cod_credi}/>
                <FormPersonalInfo values={values} errors={errors} setPopup={setPopup} />
                <FormPersonalInfoActivity values={values} errors={errors} />
            </LandscapeTablet>
            <LandscapePhonesAndSmaller>
            {renderTitle()}
                <AplicantOptions values={values} errors={errors} pwsCodCredi={values.pws_cod_credi}/>
                <FormPersonalInfoMobile values={values} errors={errors} setPopup={setPopup} />
                <FormPersonalInfoActivityMobile values={values} errors={errors}/>
            </LandscapePhonesAndSmaller>
        </>
    )
}
